import type { WaitlistEntry, WaitlistTable } from "./api";

export function remainingTableCapacity(table: WaitlistTable, entries: WaitlistEntry[], exceptEntryId?: string): number {
  if (!table.is_active || table.host_mode === "none") return 0;
  const shared = table.host_mode ? table.host_mode === "shared" : table.shared_seating;
  if (table.capacity_allocations) {
    const ownReservation = entries.find(entry => entry.id === exceptEntryId)?.reservation_id;
    const allocations = table.capacity_allocations.filter(allocation =>
      (!exceptEntryId || allocation.entry_id !== exceptEntryId) &&
      (!ownReservation || allocation.reservation_id !== ownReservation)
    );
    const occupants = allocations.filter(allocation => allocation.table_id === table.id);
    if (!shared && occupants.length > 0) return 0;
    if (!shared && table.guest_group && table.group_capacity !== undefined) {
      const group = allocations.filter(allocation => allocation.group_name === table.guest_group);
      const unassigned = group.filter(allocation => !allocation.table_id && allocation.reservation_id).length;
      const assigned = new Set(group.map(allocation => allocation.table_id).filter(Boolean)).size;
      if (unassigned > 0 && assigned + unassigned >= table.group_capacity) return 0;
    }
    return Math.max(0, table.max_capacity - occupants.reduce((total, allocation) => total + allocation.party_size, 0));
  }
  const occupants = entries.filter(entry => entry.id !== exceptEntryId && (
    (entry.status === "seated" && entry.seated_table_id === table.id) ||
    (["waiting", "notified", "final_call"].includes(entry.status) && entry.held_table_id === table.id)
  ));
  if (!shared && occupants.length) return 0;
  return Math.max(0, table.max_capacity - occupants.reduce((total, entry) => total + entry.party_size, 0));
}

export function roomAllowsIntent(room: string, hostNotes: string | null): boolean {
  const intent = hostNotes?.match(/^\[Intent:\s*([^\]]+)\]/i)?.[1]?.toLowerCase() || "";
  if (intent.includes("food")) return room === "lounge";
  if (intent.includes("cocktail")) return room === "lounge" || room === "parlor";
  return ["lounge", "library", "parlor"].includes(room);
}

export function tableFitsParty(table: WaitlistTable, entries: WaitlistEntry[], partySize: number, hostNotes: string | null, entryId?: string): boolean {
  return partySize >= (table.min_party ?? 1) &&
    remainingTableCapacity(table, entries, entryId) >= partySize && roomAllowsIntent(table.room_id, hostNotes);
}

export function arrivalWindowFulfilled(entry: WaitlistEntry): boolean {
  return entry.arrived_count === entry.party_size && !!entry.full_party_arrived_at && !!entry.hold_expires_at &&
    Date.parse(entry.full_party_arrived_at) <= Date.parse(entry.hold_expires_at);
}

export function holdCountdown(entry: WaitlistEntry, serverNow: number): string | null {
  if (!entry.held_table_id || entry.status === "seated") return null;
  if (!entry.notified_at || !entry.hold_expires_at) return "Table held · arrival clock has not started";
  if (arrivalWindowFulfilled(entry)) return "Entire party arrived within the arrival window";
  const seconds = Math.ceil((Date.parse(entry.hold_expires_at) - serverNow) / 1000);
  if (!Number.isFinite(seconds)) return "Hold deadline unavailable — refresh";
  if (seconds <= 0) return "Hold expired — extend or release explicitly";
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")} for entire party to arrive`;
}

export function notificationLabel(entry: WaitlistEntry): string | null {
  if (!entry.notification_status) return null;
  const names: Record<string, string> = {
    sent: "Text accepted by provider", accepted: "Text accepted by provider", delivered: "Text delivered",
    failed: "Text failed — contact guest", delivery_failed: "Text delivery failed — contact guest", skipped: "Text skipped — contact guest", unknown: "Text outcome unknown — check before retrying",
    pending: "Text pending — do not resend", sending: "Text pending — do not resend", manual: "Guest notified by host",
  };
  return `${names[entry.notification_status] || `Notification: ${entry.notification_status}`}${entry.notification_error ? ` · ${entry.notification_error}` : ""}`;
}

export function canSendFinalCall(entry: WaitlistEntry): boolean {
  if (entry.status !== "notified" && entry.status !== "final_call") return false;
  return entry.final_call_status ? ["failed", "skipped"].includes(entry.final_call_status) : entry.status === "notified";
}

export function finalCallLabel(entry: WaitlistEntry): string | null {
  if (!entry.final_call_status) return null;
  const labels: Record<string, string> = {
    failed: "Final Call failed — retry available", skipped: "Final Call skipped — retry available",
    pending: "Final Call pending — do not resend", sending: "Final Call pending — do not resend",
    unknown: "Final Call outcome unknown — check before retrying",
    accepted: "Final Call accepted by provider", sent: "Final Call accepted by provider",
    delivered: "Final Call delivered", delivery_failed: "Final Call delivery failed — contact guest directly",
  };
  return `${labels[entry.final_call_status] || `Final Call: ${entry.final_call_status}`}${entry.final_call_error ? ` · ${entry.final_call_error}` : ""}`;
}

export function minimumGuestsPresent(partySize: number): number {
  return Math.ceil(partySize * 0.75);
}

export function hasMinimumGuestsPresent(partySize: number, present: number): boolean {
  return Number.isInteger(partySize) && partySize > 0 && Number.isInteger(present) && present <= partySize && present >= minimumGuestsPresent(partySize);
}
